import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "../../auth/AuthContext";
import PrimaryButton from "../../PrimaryButton";
import Footer from "../home/Footer";
import { useSearchParams } from "react-router-dom";
import { Box, ButtonBase, Typography, IconButton, Modal, Fade, Backdrop } from "@mui/material";
import Android from "../../assets/apps/google-play-store.png";
import Ios from "../../assets/apps/apple-store.png";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import StemProject from "./StemProject";

const Service = () => {
  const [services, setServices] = useState([]);
  const [department, setDepartment] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authValue = useAuth();
  const { getData } = authValue;
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [csr, setCsr] = useState([]);
  const [csrEvent, setCsrEvent] = useState([]);
  
  // State for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // Refs for scroll containers
  const servicesScrollRef = useRef(null);
  const projectsScrollRef = useRef(null);

  // Get the code from URL parameters once on component mount
  const codeFromUrl = searchParams.get("code");

  useEffect(() => {
    // Set selected service from URL parameters
    if (codeFromUrl) {
      setSelectedService(codeFromUrl);
    }
  }, [codeFromUrl]);

  useEffect(() => {
    async function fetchData() {
      // Don't fetch if no service is selected yet
      if (!selectedService) {
        setLoading(false);
        return;
      }

      if (typeof getData !== "function") {
        const msg =
          "getData is not available. Check: 1) AuthProvider wrapping, 2) Import path, 3) Context value.";
        console.error(msg);
        setError(msg);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const responseClints = await getData("proccess.php", "our_clients_V2"); // Fetch adverts data
        if (responseClints.success) {
          setClients(responseClints.data);
        } else {
          console.error(
            responseClints.message || "Failed to load adverts data"
          );
        }

        // Fetch services and department in parallel
        const [servicesResponse, departmentResponse, projectResponse, csrResponse, csrEventResponse] =
          await Promise.all([
            getData("proccess.php", "services"),
            getData("proccess.php", "department"),
            getData("proccess.php", "projects_V2"),
            getData("proccess.php", "csr_V2"),
            getData("proccess.php", "steam_projects_V2"),
          ]);

        if (csrEventResponse.success && Array.isArray(csrEventResponse.data)) {
          setCsrEvent(csrEventResponse.data);
        }

        if (csrResponse.success && Array.isArray(csrResponse.data)) {
          setCsr(csrResponse.data);
        }

        if (servicesResponse.success) {
          const foundServices = servicesResponse.data.filter(
            (service) => service.department == selectedService
          );
          const sortedServices = [...foundServices].sort((a, b) => {
            const orderA = Number(a.orderNumber) || 0;
            const orderB = Number(b.orderNumber) || 0;
            return orderA - orderB;
          });
          setServices(sortedServices);
        } else {
          setError(servicesResponse.message || "Failed to fetch services");
        }

        if (departmentResponse.success) {
          const foundDepartment = departmentResponse.data.find(
            (dep) => dep.id == selectedService
          );
          if (foundDepartment) {
            setDepartment(foundDepartment);
          } else {
            setError("Department not found");
          }
        } else {
          setError(departmentResponse.message || "Failed to fetch department");
        }

        if (projectResponse.success) {
          const foundProjects = projectResponse.data.filter(
            (project) => project.department == selectedService
          );
          const sortedProjects = [...foundProjects].sort((a, b) => {
            const orderA = Number(a.orderNumber) || 0;
            const orderB = Number(b.orderNumber) || 0;
            return orderA - orderB;
          });
          setProjects(sortedProjects);
        } else {
          setError(servicesResponse.message || "Failed to fetch services");
        }
      } catch (err) {
        console.error("Fetch error in Service:", err);
        setError(err.message || "Network error");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [getData, selectedService]); // This effect depends on selectedService

  // Modal handlers
  const handleOpenModal = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  // Scroll functions for services
  const scrollServicesLeft = () => {
    if (servicesScrollRef.current) {
      servicesScrollRef.current.scrollBy({
        left: -400,
        behavior: 'smooth'
      });
    }
  };

  const scrollServicesRight = () => {
    if (servicesScrollRef.current) {
      servicesScrollRef.current.scrollBy({
        left: 400,
        behavior: 'smooth'
      });
    }
  };

  // Scroll functions for projects
  const scrollProjectsLeft = () => {
    if (projectsScrollRef.current) {
      projectsScrollRef.current.scrollBy({
        left: -600,
        behavior: 'smooth'
      });
    }
  };

  const scrollProjectsRight = () => {
    if (projectsScrollRef.current) {
      projectsScrollRef.current.scrollBy({
        left: 600,
        behavior: 'smooth'
      });
    }
  };

  // Show loading only when we have a selectedService and are actually loading data
  if (loading && selectedService) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        Loading services...
      </div>
    );
  }

  // Show message when no service is selected
  if (!selectedService) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        Please select a service from the URL parameters.
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingTop: "100px",
          backgroundColor: "#ffffffff",
        }}
      >
        {department ? (
          department.department_name == "Steam" ||
          department.department_name == "STEAM" ? (
            <div style={{ width: "100%" }}>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#3b76b2",
                  minHeight: "50vh",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: "1200px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    padding: "2rem",
                    overflow: "hidden",
                  }}
                >
                  
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "100%", ms: "100%" },
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "2rem",
                      alignItems: "start",
                      justifyContent: "center",
                    }}
                  >
                    {clients.map((client) => (
                      <Box
                        key={client.id}
                        sx={{
                          width: "200px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "1rem",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        data-aos="zoom-in" // Animation for client cards
                        data-aos-delay={`${200 + 1 * 100}`} // Staggered delay
                      >
                        <Box
                          className="box-hover"
                          sx={{
                            position: "relative",
                            width: { xs: "300px", sm: "200px" },
                            height: { xs: "300px", sm: "200px" },
                            borderRadius: "40px",
                            overflow: "hidden",
                            margin: { xs: "0", sm: "0" },
                          }}
                        >
                          <ButtonBase
                            href={`../../modules/category/${client.categories}/?cat=${client.categories}`}
                            sx={{ width: "100%", height: "100%" }}
                          >
                            <img
                              src={
                                client.image
                                  ? `https://mindsinaction.com.na/assets/media/pictures/${client.image}`
                                  : null
                              }
                              alt="STEAM Logo"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </ButtonBase>
                        </Box>
                        <Typography
                          variant="h6"
                          textAlign="center"
                          color="rgb(217, 245, 212)"
                          fontWeight={700}
                        >
                          {client.title}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </div>
              </div>

              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#e7f1f5",
                  minHeight: "50vh",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: "1200px",
                    marginTop: "3rem",
                    padding: "1rem",
                  }}
                >
                  <h1 className="primary-header">Recent Projects</h1>
                </div>
                <StemProject csr={csr} csrEvent={csrEvent} />
              </div>
            </div>
          ) : (
            <div style={{ width: "100%" }}>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#3b76b2",
                  minHeight: "50vh",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: "1200px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    padding: "2rem",
                    position: "relative",
                  }}
                >
                  {error && (
                    <p style={{ color: "red", textAlign: "center" }}>
                      Error: {error}
                    </p>
                  )}
                  
                  {/* Services Section with Scroll Controls */}
                  {services.length > 0 && (
                    <Box sx={{ 
                      position: "relative", 
                      display: "flex", 
                      alignItems: "center",
                      width: "100%",
                    }}>
                      {/* Left Scroll Button for Services */}
                      <IconButton
                        onClick={scrollServicesLeft}
                        sx={{
                          position: "absolute",
                          left: -20,
                          zIndex: 10,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                          display: { xs: "flex", sm: "flex" },
                          boxShadow: 2,
                        }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>

                      {/* Services Container */}
                      <ul
                        ref={servicesScrollRef}
                        style={{
                          listStyle: "none",
                          padding: 0,
                          width: "100%",
                          display: "flex",
                          overflowX: "auto",
                          scrollBehavior: "smooth",
                          overflowY: "hidden",
                          gap: "1rem",
                          padding: '1rem',
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >
                        {services.map((service, index) => (
                          // Services Cards
                          <li 
                            className="service" 
                            key={service.id || index}
                            style={{
                              flex: "0 0 auto",
                              width: "350px",
                              height: '420px'
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                height: "auto",
                                overflow: "hidden",
                              }}
                            >
                              <img
                                src={`https://mindsinaction.com.na/assets/media/pictures/${service.image}`}
                                alt="services"
                                style={{
                                  width: "100%",
                                }}
                              />
                            </div>
                            <div className="serviceCard">
                              <h3
                                style={{
                                  marginTop: "1rem",
                                  background:
                                    "linear-gradient(to right , #32a059, #32a059, transparent)",
                                  width: "90%",
                                  padding: "1rem",
                                  color: "#fff",
                                  fontWeight: "700",
                                  fontSize: "1.3rem"
                                }}
                              >
                                {service.service_name || `Service ${index + 1}`}
                              </h3>
                              <div
                                style={{
                                  padding: "1rem",
                                  width: "100%",
                                  height: "100%",
                                }}
                              >
                                <Typography
                                  dangerouslySetInnerHTML={{
                                    __html: service.short_description || "",
                                  }}
                                  variant={{ xs: "body1", md: "h6" }}
                                  color="#000000ff"
                                  sx={{
                                    fontSize: { xs: "1rem", md: "1.25rem" },
                                    lineHeight: 1.4,
                                  }}
                                />
                                <br />
                                <br />
                                <ButtonBase
                                  href="/contact"
                                  sx={{
                                    padding: ".5rem 1rem",
                                    backgroundColor: "#3b76b2",
                                    color: "#fff",
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    "&:hover": {
                                      backgroundColor: "#335d8e",
                                    },
                                  }}
                                >
                                  <Typography>Get Started &rarr;</Typography>
                                </ButtonBase>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>

                      {/* Right Scroll Button for Services */}
                      <IconButton
                        onClick={scrollServicesRight}
                        sx={{
                          position: "absolute",
                          right: -20,
                          zIndex: 10,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                          display: { xs: "flex", sm: "flex" },
                          boxShadow: 2,
                        }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                  )}
                  
                  {services.length === 0 && !loading && !error && (
                    <p style={{ textAlign: "center" }}>
                      No services available for this department.
                    </p>
                  )}
                </div>
              </div>

              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#e7f1f5",
                  minHeight: "50vh",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: "1200px",
                    marginTop: "3rem",
                    padding: "1rem",
                  }}
                >
                  <h1 className="primary-header">Recent Projects</h1>
                </div>
                <div
                  style={{
                    width: "100%",
                    maxWidth: "1200px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    padding: "2rem",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {error && (
                    <p style={{ color: "red", textAlign: "center" }}>
                      Error: {error}
                    </p>
                  )}
                  
                  {/* Projects Section with Scroll Controls */}
                  {projects.length > 0 && (
                    <Box sx={{ 
                      position: "relative", 
                      display: "flex", 
                      alignItems: "center",
                      width: "100%",
                    }}>
                      {/* Left Scroll Button for Projects */}
                      <IconButton
                        onClick={scrollProjectsLeft}
                        sx={{
                          position: "absolute",
                          left: -20,
                          zIndex: 10,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                          display: { xs: "flex", sm: "flex" },
                          boxShadow: 2,
                        }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>

                      {/* Projects Container */}
                      <div
                        ref={projectsScrollRef}
                        style={{
                          listStyle: "none",
                          padding: 0,
                          width: "100%",
                          display: "flex",
                          gap: "1rem",
                          overflowX: "auto",
                          scrollBehavior: "smooth",
                          overflowY: "hidden",
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >
                        {projects.map((project, index) => (
                          // Projects Cards - Made clickable
                          <div
                            onClick={() => handleOpenModal(project)}
                            style={{
                              position: "relative",
                              width: "700px",
                              minHeight: "350px",
                              flex: "0 0 auto",
                              display: "flex",
                              flexWrap: "wrap-reverse",
                              border: ".1rem solid #32a059",
                              borderRadius: "1rem",
                              overflow: "hidden",
                              backgroundColor: "#fff",
                              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                              cursor: "pointer",
                              transition: "transform 0.2s, box-shadow 0.2s",
                            }}
                            key={project.id || index}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.02)";
                              e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)";
                            }}
                          >
                            {/* Click indicator */}
                            <div style={{
                              position: "absolute",
                              top: "10px",
                              right: "10px",
                              zIndex: 5,
                              backgroundColor: "rgba(255,255,255,0.9)",
                              borderRadius: "20px",
                              padding: "4px 8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "0.8rem",
                              color: "#3b76b2",
                              border: "1px solid #3b76b2"
                            }}>
                              <LaunchIcon sx={{ fontSize: "16px" }} />
                              <span>Click for details</span>
                            </div>

                            {/* Left content section */}
                            <div
                              style={{
                                flex: 1,
                                minWidth: "250px",
                                maxWidth: "350px",
                              }}
                            >
                              <h3
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  marginTop: "0.5rem",
                                  background:
                                    "linear-gradient(to right , #32a059, #32a058d8, transparent)",
                                  width: "80%",
                                  padding: "0.75rem",
                                  color: "#fff",
                                  fontWeight: "700",
                                  fontSize: "1.1rem",
                                }}
                              >
                                {project.name || `Service ${index + 1}`}
                              </h3>
                              <div
                                style={{
                                  width: "100%",
                                  minHeight: "280px",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: '0.75rem'
                                }}
                              >
                                <div style={{height: '40px'}}></div>
                                {project.project_type === "mobile" && (
                                  <div style={{
                                    width: '100%',
                                    textAlign: 'center',
                                    marginBottom: '0.5rem'
                                  }}>
                                    <h2 className="primary-header" style={{fontSize: '1.5rem'}}>Download</h2>
                                    <h4 style={{fontSize: '1.1rem'}}>Our <span style={{fontWeight: '700'}}>APP</span></h4>
                                  </div>
                                )}
                                <Typography
                                  dangerouslySetInnerHTML={{
                                    __html: project.description?.substring(0, 150) + (project.description?.length > 150 ? "..." : "") || "",
                                  }}
                                  variant="body2"
                                  color="#000000ff"
                                  fontSize={"0.9rem"}
                                  padding={"0.75rem"}
                                  maxHeight={"180px"}
                                  overflowX={"hidden"}
                                  overflowY={"auto"}
                                  sx={{
                                    '& p': { margin: '0.5rem 0' }
                                  }}
                                />
                                {/* Show limited preview in card */}
                                <div style={{
                                  marginTop: "0.5rem",
                                  color: "#3b76b2",
                                  fontSize: "0.85rem",
                                  fontWeight: "600"
                                }}>
                                  Click to view full details →
                                </div>
                              </div>
                            </div>
                            {/* Right image section */}
                            <div
                              style={{
                                flex: 1,
                                minWidth: "200px",
                                maxWidth: "280px",
                                height: "350px"
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  overflow: "hidden",
                                }}
                              >
                                <img
                                  src={`https://mindsinaction.com.na/assets/media/pictures/${project.image}`}
                                  alt="services"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Right Scroll Button for Projects */}
                      <IconButton
                        onClick={scrollProjectsRight}
                        sx={{
                          position: "absolute",
                          right: -20,
                          zIndex: 10,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                          display: { xs: "flex", sm: "flex" },
                          boxShadow: 2,
                        }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                  )}
                  
                  {projects.length === 0 && !loading && !error && (
                    <p style={{ textAlign: "center" }}>
                      No services available for this department.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        ) : (
          <p> No services available</p>
        )}
      </div>

      {/* Project Details Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: '1rem',
            boxShadow: 24,
            overflow: 'auto',
            outline: 'none',
          }}>
            {/* Modal Header with Close Button */}
            <div style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 1.5rem',
              backgroundColor: '#fff',
              borderBottom: '1px solid #e0e0e0',
            }}>
              <Typography variant="h5" sx={{ fontWeight: '700', color: '#32a059' }}>
                {selectedProject?.name}
              </Typography>
              <IconButton onClick={handleCloseModal} sx={{ color: '#666' }}>
                <CloseIcon />
              </IconButton>
            </div>

            {/* Modal Content */}
            {selectedProject && (
              <Box sx={{ padding: '2rem' }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: '2rem',
                }}>
                  {/* Left Column - Project Image */}
                  <div style={{
                    flex: 1,
                    minWidth: '300px',
                  }}>
                    <div style={{
                      width: '100%',
                      height: '400px',
                      borderRadius: '1rem',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}>
                      <img
                        src={`https://mindsinaction.com.na/assets/media/pictures/${selectedProject.image}`}
                        alt={selectedProject.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                  </div>

                  {/* Right Column - Project Details */}
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.5rem',
                  }}>
                    {/* Full Description */}
                    <div>
                      <Typography variant="h6" sx={{ fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
                        Project Description
                      </Typography>
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: selectedProject.description || "No description available.",
                        }}
                        sx={{
                          fontSize: '1rem',
                          lineHeight: 1.6,
                          color: '#555',
                          '& p': { margin: '1rem 0' },
                        }}
                      />
                    </div>

                    {/* Download/View Links */}
                    {selectedProject.project_type === "mobile" ? (
                      <div>
                        <Typography variant="h6" sx={{ fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
                          Download App
                        </Typography>
                        <div style={{
                          display: 'flex',
                          gap: '2rem',
                          flexWrap: 'wrap',
                        }}>
                          {selectedProject.android_link && (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '1rem',
                            }}>
                              <ButtonBase href={selectedProject.android_link} target="_blank">
                                <img src={Android} alt="Google Play" style={{ width: '150px' }} />
                              </ButtonBase>
                              <div style={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="#666">Android</Typography>
                                <div style={{ width: '100px', height: '100px', marginTop: '0.5rem' }}>
                                  <img 
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(selectedProject.android_link)}`} 
                                    alt="QR Code" 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          {selectedProject.ios_link && (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '1rem',
                            }}>
                              <ButtonBase href={selectedProject.ios_link} target="_blank">
                                <img src={Ios} alt="App Store" style={{ width: '150px' }} />
                              </ButtonBase>
                              <div style={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="#666">iOS</Typography>
                                <div style={{ width: '100px', height: '100px', marginTop: '0.5rem' }}>
                                  <img 
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(selectedProject.ios_link)}`} 
                                    alt="QR Code" 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      selectedProject.general_link && (
                        <div>
                          <Typography variant="h6" sx={{ fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
                            View Project
                          </Typography>
                          <div style={{
                            display: 'flex',
                            gap: '2rem',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}>
                            <ButtonBase 
                              href={selectedProject.general_link} 
                              target="_blank"
                              sx={{
                                padding: ".75rem 2rem",
                                backgroundColor: "#3b76b2",
                                color: "#fff",
                                borderRadius: "8px",
                                textTransform: "none",
                                fontSize: "1.1rem",
                                "&:hover": {
                                  backgroundColor: "#335d8e",
                                },
                              }}
                            >
                              Visit Project Website →
                            </ButtonBase>
                            <div style={{ textAlign: 'center' }}>
                              <Typography variant="body2" color="#666">Scan QR Code</Typography>
                              <div style={{ width: '120px', height: '120px', marginTop: '0.5rem' }}>
                                <img 
                                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(selectedProject.general_link)}`} 
                                  alt="QR Code" 
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )}

                    {/* Additional Project Info */}
                    {(selectedProject.start_date || selectedProject.end_date || selectedProject.technologies) && (
                      <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '0.5rem' }}>
                        <Typography variant="h6" sx={{ fontWeight: '600', marginBottom: '0.5rem', color: '#333' }}>
                          Project Details
                        </Typography>
                        {selectedProject.start_date && (
                          <Typography variant="body2" color="#666">
                            <strong>Start Date:</strong> {new Date(selectedProject.start_date).toLocaleDateString()}
                          </Typography>
                        )}
                        {selectedProject.end_date && (
                          <Typography variant="body2" color="#666">
                            <strong>End Date:</strong> {new Date(selectedProject.end_date).toLocaleDateString()}
                          </Typography>
                        )}
                        {selectedProject.technologies && (
                          <Typography variant="body2" color="#666">
                            <strong>Technologies:</strong> {selectedProject.technologies}
                          </Typography>
                        )}
                      </div>
                    )}
                  </div>
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* footer */}
      <Footer />
    </>
  );
};

export default Service;